import React, { useState, useEffect, useRef } from 'react';
import Icon from 'components/AppIcon';

const StopwatchTimer = ({ onSave, defaultRate }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [pausedAt, setPausedAt] = useState(null);
  const [totalPausedMs, setTotalPausedMs] = useState(0);
  const [billable, setBillable] = useState(true);
  const [caseId, setCaseId] = useState('');
  const [caseName, setCaseName] = useState('');
  const [activityDescription, setActivityDescription] = useState('');
  const [hourlyRate, setHourlyRate] = useState(defaultRate || 0);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    setHourlyRate(defaultRate || 0);
  }, [defaultRate]);

  useEffect(() => {
    if (isRunning && !isPaused) {
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(intervalRef?.current);
    }
    return () => clearInterval(intervalRef?.current);
  }, [isRunning, isPaused]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h)?.padStart(2, '0')}:${String(m)?.padStart(2, '0')}:${String(s)?.padStart(2, '0')}`;
  };

  const handleStart = () => {
    if (!isRunning) {
      setStartTime(new Date());
      setElapsedSeconds(0);
      setTotalPausedMs(0);
      setIsRunning(true);
      setIsPaused(false);
      setShowSaveForm(false);
    }
  };

  const handlePause = () => {
    if (isRunning && !isPaused) {
      setIsPaused(true);
      setPausedAt(new Date());
    } else if (isPaused) {
      const pauseDuration = new Date() - pausedAt;
      setTotalPausedMs(prev => prev + pauseDuration);
      setIsPaused(false);
      setPausedAt(null);
    }
  };

  const handleStop = () => {
    if (isRunning) {
      setIsRunning(false);
      setIsPaused(false);
      setShowSaveForm(true);
    }
  };

  const handleReset = () => {
    setIsRunning(false);
    setIsPaused(false);
    setElapsedSeconds(0);
    setStartTime(null);
    setShowSaveForm(false);
    setActivityDescription('');
    setCaseId('');
    setCaseName('');
  };

  const handleSave = () => {
    if (!activityDescription?.trim()) return;
    const durationMinutes = Math.max(1, Math.round(elapsedSeconds / 60));
    onSave({
      caseId,
      caseName,
      durationMinutes,
      hourlyRate: Number(hourlyRate),
      billable,
      activityDescription,
      startedAt: startTime?.toISOString(),
      endedAt: new Date()?.toISOString()
    });
    handleReset();
  };

  const billableAmount = ((elapsedSeconds / 3600) * Number(hourlyRate))?.toFixed(2);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Timer Display */}
      <div className="bg-surface rounded-2xl border border-border p-8">
        <div className="text-center mb-8">
          <div className={`text-6xl font-mono font-bold tracking-wider mb-2 ${
            isRunning && !isPaused ? 'text-primary' : isPaused ? 'text-warning-600' : 'text-text-primary'
          }`}>
            {formatTime(elapsedSeconds)}
          </div>
          <div className="flex items-center justify-center gap-2 text-sm text-text-secondary">
            {isRunning && !isPaused && (
              <><span className="w-2 h-2 bg-success-500 rounded-full animate-pulse"></span> Running</>
            )}
            {isPaused && (
              <><span className="w-2 h-2 bg-warning-500 rounded-full"></span> Paused</>
            )}
            {!isRunning && !isPaused && elapsedSeconds === 0 && (
              <><span className="w-2 h-2 bg-secondary-300 rounded-full"></span> Ready to start</>
            )}
            {!isRunning && elapsedSeconds > 0 && (
              <><span className="w-2 h-2 bg-secondary-400 rounded-full"></span> Stopped</>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {!isRunning ? (
            <button
              onClick={handleStart}
              className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-body-medium hover:bg-primary-700 transition-colors"
            >
              <Icon name="Play" size={18} />
              Start
            </button>
          ) : (
            <>
              <button
                onClick={handlePause}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-body-medium transition-colors ${
                  isPaused
                    ? 'bg-primary text-white hover:bg-primary-700' :'bg-warning-50 text-warning-700 border border-warning-200 hover:bg-warning-100'
                }`}
              >
                <Icon name={isPaused ? 'Play' : 'Pause'} size={18} />
                {isPaused ? 'Resume' : 'Pause'}
              </button>
              <button
                onClick={handleStop}
                className="flex items-center gap-2 bg-error-50 text-error-700 border border-error-200 px-5 py-3 rounded-xl font-body-medium hover:bg-error-100 transition-colors"
              >
                <Icon name="Square" size={18} />
                Stop
              </button>
            </>
          )}
          {(elapsedSeconds > 0 || showSaveForm) && (
            <button
              onClick={handleReset}
              className="flex items-center gap-2 text-text-secondary border border-border px-4 py-3 rounded-xl font-body-medium hover:bg-secondary-50 transition-colors"
            >
              <Icon name="RotateCcw" size={16} />
            </button>
          )}
        </div>

        {/* Billable Toggle */}
        <div className="flex items-center justify-between p-3 bg-secondary-50 rounded-xl">
          <div className="flex items-center gap-2">
            <Icon name="DollarSign" size={16} className="text-text-secondary" />
            <span className="text-sm font-body-medium text-text-primary">Billable</span>
          </div>
          <button
            onClick={() => setBillable(!billable)}
            className={`relative w-11 h-6 rounded-full transition-colors ${
              billable ? 'bg-primary' : 'bg-secondary-300'
            }`}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              billable ? 'translate-x-5' : 'translate-x-0.5'
            }`} />
          </button>
        </div>

        {/* Rate Display */}
        {billable && (
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-text-secondary">Estimated amount</span>
            <span className="font-body-semibold text-success-700">KES {Number(billableAmount)?.toLocaleString()}</span>
          </div>
        )}
      </div>
      {/* Entry Details Form */}
      <div className="bg-surface rounded-2xl border border-border p-6">
        <h3 className="font-heading-semibold text-text-primary mb-4">Entry Details</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Case / Matter</label>
            <input
              type="text"
              value={caseName}
              onChange={e => setCaseName(e?.target?.value)}
              placeholder="Search or enter case name..."
              className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Case ID (optional)</label>
            <input
              type="text"
              value={caseId}
              onChange={e => setCaseId(e?.target?.value)}
              placeholder="e.g. CASE-2024-001"
              className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Hourly Rate (KES)</label>
            <input
              type="number"
              value={hourlyRate}
              onChange={e => setHourlyRate(e?.target?.value)}
              min="0"
              className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">
              Activity Description <span className="text-error-500">*</span>
            </label>
            <textarea
              value={activityDescription}
              onChange={e => setActivityDescription(e?.target?.value)}
              placeholder="Describe the work performed..."
              rows={4}
              className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          {showSaveForm && (
            <button
              onClick={handleSave}
              disabled={!activityDescription?.trim()}
              className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-xl font-body-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Save" size={16} />
              Save Time Entry ({formatTime(elapsedSeconds)})
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StopwatchTimer;
